<template>
  <div class="workforce-center">
    <PageHeader icon="fas fa-users-cog">
      <template #title>Workforce & Payroll Center</template>
      <template #subtitle
        >Kelola ketenagakerjaan, kehadiran, kepatuhan, dan penggajian klinik
        dalam satu alur</template
      >
      <template #actions
        ><b-button class="btn-primary" @click="openPeriod"
          ><i class="fas fa-plus"></i> Buat Periode Payroll</b-button
        ></template
      >
    </PageHeader>

    <WorkspaceTabs
      :value="activeTab"
      :items="tabs"
      aria-label="Navigasi Workforce & Payroll"
      @change="selectTab"
    />

    <template v-if="activeTab === 'dashboard'">
      <div class="wf-kpis">
        <article>
          <i class="fas fa-user-check"></i>
          <div>
            <small>Karyawan aktif</small
            ><b>{{ summary.active_employees || 0 }}</b>
          </div>
        </article>
        <article>
          <i class="fas fa-fingerprint"></i>
          <div>
            <small>Hadir hari ini</small><b>{{ summary.present_today || 0 }}</b>
          </div>
        </article>
        <article>
          <i class="fas fa-calendar-minus"></i>
          <div>
            <small>Cuti menunggu</small><b>{{ summary.pending_leave || 0 }}</b>
          </div>
        </article>
        <article class="warning">
          <i class="fas fa-id-badge"></i>
          <div>
            <small>STR/SIP segera berakhir</small
            ><b>{{ summary.expiring_credentials || 0 }}</b>
          </div>
        </article>
      </div>
      <div class="wf-grid">
        <section class="wf-card">
          <header>
            <div>
              <small>PAYROLL</small>
              <h3>Periode terbaru</h3>
            </div>
          </header>
          <PeriodTable :items="periods" @open="openDetail" />
        </section>
        <section class="wf-card">
          <header>
            <div>
              <small>KREDENSIAL KLINIS</small>
              <h3>Perlu perhatian</h3>
            </div>
          </header>
          <div v-if="!credentialAlerts.length" class="wf-empty">
            Tidak ada STR/SIP yang berakhir dalam 90 hari.
          </div>
          <div
            v-for="row in credentialAlerts"
            :key="row.id"
            class="credential-row"
          >
            <div>
              <b>{{ row.name }}</b
              ><small>{{ row.position_name || "Karyawan" }}</small>
            </div>
            <span>{{ credentialDate(row) }}</span>
          </div>
        </section>
      </div>
    </template>

    <section v-if="activeTab === 'payroll'" class="wf-card">
      <header>
        <div>
          <small>SIKLUS PENGGAJIAN</small>
          <h3>Periode payroll</h3>
        </div>
        <button class="soft-btn" @click="loadPeriods">
          <i class="fas fa-sync"></i> Muat ulang
        </button>
      </header>
      <PeriodTable :items="periods" @open="openDetail" />
    </section>

    <section v-if="activeTab === 'employees'" class="wf-card">
      <header>
        <div>
          <small>DATA KETENAGAKERJAAN</small>
          <h3>Master karyawan & kepatuhan</h3>
        </div>
        <div class="row-actions">
          <button class="soft-btn" @click="$router.push('/masters/departemens/list')">
            <i class="fas fa-sitemap"></i> Departemen
          </button>
          <button class="soft-btn" @click="$router.push('/masters/positions/list')">
            <i class="fas fa-id-badge"></i> Jabatan
          </button>
          <button class="primary-btn" @click="$router.push('/human-resource/employee/add')">
            <i class="fas fa-user-plus"></i> Tambah Karyawan
          </button>
        </div>
      </header>
      <div class="wf-subtabs">
        <button
          :class="{ active: employeeView === 'directory' }"
          @click="employeeView = 'directory'"
        >
          Direktori & Profil
        </button>
        <button
          :class="{ active: employeeView === 'employment' }"
          @click="employeeView = 'employment'; loadEmployees()"
        >
          Payroll & Kepatuhan
        </button>
      </div>
      <EmployeeTable v-if="employeeView === 'directory'" purpose="master" />
      <template v-else>
        <div class="wf-inline-filter">
          <input
            v-model="search"
            class="wf-search"
            placeholder="Cari nama atau NIP"
            @input="searchEmployees"
          />
        </div>
        <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>NIP & Karyawan</th>
              <th>Jabatan</th>
              <th>Status</th>
              <th>Gaji pokok</th>
              <th>BPJS</th>
              <th>STR/SIP</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in employees" :key="row.id">
              <td>
                <b>{{ row.name }}</b
                ><small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ row.position_name || "-" }}</td>
              <td>
                <span class="status-chip">{{
                  employmentLabel(row.employment_status)
                }}</span>
              </td>
              <td>{{ money(row.base_salary) }}</td>
              <td>
                {{
                  row.bpjs_health_number || row.bpjs_employment_number
                    ? "Terdaftar"
                    : "Belum lengkap"
                }}
              </td>
              <td>{{ credentialDate(row) }}</td>
              <td>
                <button class="icon-btn" @click="editEmployee(row)">
                  <i class="fas fa-edit"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>
    </section>

    <section v-if="activeTab === 'time'" class="wf-card workforce-time-card">
      <header>
        <div>
          <small>KEHADIRAN & JADWAL</small>
          <h3>Waktu kerja karyawan</h3>
        </div>
      </header>
      <div class="wf-subtabs">
        <button
          v-for="item in [
            { key: 'attendance-report', label: 'Rekap Absensi' },
            { key: 'leave', label: 'Cuti & Izin' },
            { key: 'overtime', label: 'Lembur' },
            { key: 'shifts', label: 'Shift' },
          ]"
          :key="item.key"
          :class="{ active: timeView === item.key }"
          @click="timeView = item.key; loadTimeView()"
        >
          {{ item.label }}
        </button>
      </div>
      <AttendanceReport v-if="timeView === 'attendance-report'" embedded />
    </section>

    <section v-if="activeTab === 'time' && timeView === 'leave'" class="wf-card">
      <header>
        <div>
          <small>WAKTU KERJA</small>
          <h3>Cuti, izin & sakit</h3>
        </div>
        <button class="primary-btn" @click="openLeave">
          <i class="fas fa-plus"></i> Ajukan Cuti/Izin
        </button>
      </header>
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Karyawan</th>
              <th>Jenis</th>
              <th>Periode</th>
              <th>Hari</th>
              <th>Sisa Cuti</th>
              <th>Alasan</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in leaves" :key="row.id">
              <td>
                <b>{{ row.employee_name }}</b
                ><small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ leaveLabel(row.leave_type) }}</td>
              <td>{{ date(row.start_date) }} – {{ date(row.end_date) }}</td>
              <td>{{ row.days }}</td>
              <td>
                <span v-if="row.leave_type === 'annual'" class="balance-chip"
                  >{{ Number(row.leave_balance || 0) }} hari</span
                ><span v-else>-</span>
              </td>
              <td>{{ row.reason || "-" }}</td>
              <td>
                <span :class="['status-chip', row.status]">{{
                  statusLabel(row.status)
                }}</span>
              </td>
              <td>
                <div v-if="row.status === 'submitted'" class="row-actions">
                  <button
                    class="approve-mini"
                    @click="decideLeave(row.id, 'approved')"
                  >
                    <i class="fas fa-check"></i></button
                  ><button
                    class="reject-mini"
                    @click="decideLeave(row.id, 'rejected')"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <span v-else>-</span>
              </td>
            </tr>
            <tr v-if="!leaves.length">
              <td colspan="8" class="wf-empty">
                Belum ada pengajuan cuti atau izin.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'time' && timeView === 'overtime'" class="wf-card">
      <header>
        <div>
          <small>JAM KERJA TAMBAHAN</small>
          <h3>Lembur karyawan</h3>
        </div>
        <button class="primary-btn" @click="openOvertime">
          <i class="fas fa-plus"></i> Ajukan Lembur
        </button>
      </header>
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Karyawan</th>
              <th>Tanggal</th>
              <th>Waktu</th>
              <th>Durasi</th>
              <th>Nominal</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in overtimes" :key="row.id">
              <td>
                <b>{{ row.employee_name }}</b
                ><small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ date(row.work_date) }}</td>
              <td>{{ time(row.start_at) }}–{{ time(row.end_at) }}</td>
              <td>{{ row.minutes }} menit</td>
              <td>{{ money(row.approved_amount) }}</td>
              <td>
                <span :class="['status-chip', row.status]">{{
                  statusLabel(row.status)
                }}</span>
              </td>
              <td>
                <div v-if="row.status === 'submitted'" class="row-actions">
                  <button class="approve-mini" @click="decideOvertime(row)">
                    <i class="fas fa-check"></i></button
                  ><button class="reject-mini" @click="rejectOvertime(row.id)">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'time' && timeView === 'shifts'" class="wf-card">
      <header>
        <div>
          <small>PENJADWALAN</small>
          <h3>Master shift kerja</h3>
        </div>
        <div class="row-actions">
          <button class="soft-btn" @click="openShiftAssignment">
            <i class="fas fa-calendar-plus"></i> Atur Jadwal</button
          ><button class="primary-btn" @click="openShift">
            <i class="fas fa-plus"></i> Tambah Shift
          </button>
        </div>
      </header>
      <div class="shift-grid">
        <article v-for="row in shifts" :key="row.id">
          <i class="fas fa-clock"></i>
          <div>
            <b>{{ row.name }}</b
            ><small
              >{{ row.code }} · {{ String(row.start_time).slice(0, 5) }}–{{
                String(row.end_time).slice(0, 5)
              }}</small
            ><span
              >Istirahat {{ row.break_minutes }} menit · Toleransi
              {{ row.late_tolerance_minutes }} menit</span
            >
          </div>
        </article>
      </div>
      <div class="table-responsive shift-assignments">
        <table>
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Karyawan</th>
              <th>Shift</th>
              <th>Jam Kerja</th>
              <th>Catatan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in shiftAssignments" :key="row.id">
              <td>{{ date(row.work_date) }}</td>
              <td>
                {{ row.employee_name
                }}<small>{{ row.employee_number_id }}</small>
              </td>
              <td>
                <b>{{ row.shift_name }}</b
                ><small>{{ row.shift_code }}</small>
              </td>
              <td>
                {{ String(row.start_time).slice(0, 5) }}–{{
                  String(row.end_time).slice(0, 5)
                }}
              </td>
              <td>{{ row.notes || "-" }}</td>
            </tr>
            <tr v-if="!shiftAssignments.length">
              <td colspan="5" class="wf-empty">Belum ada penugasan shift.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'compensation'" class="wf-card">
      <header>
        <div>
          <small>KOMPONEN GAJI</small>
          <h3>Kompensasi rutin karyawan</h3>
        </div>
        <div class="row-actions">
          <button class="soft-btn" @click="openCompensationType()">
            <i class="fas fa-layer-group"></i> Tambah Komponen
          </button>
          <button class="primary-btn" @click="openCompensationAssignment()">
            <i class="fas fa-user-plus"></i> Tetapkan ke Karyawan
          </button>
        </div>
      </header>
      <div class="notice compensation-notice">
        <i class="fas fa-info-circle"></i>
        <div>
          <b>Satu sumber komponen payroll</b>
          <p>
            Hanya komponen berstatus aktif yang masuk kalkulasi Workforce.
            Komponen lama tetap tersimpan sebagai histori sampai dipetakan dan
            diaktifkan di sini.
          </p>
        </div>
      </div>
      <div class="wf-subtabs">
        <button
          :class="{ active: compensationView === 'assignments' }"
          @click="compensationView = 'assignments'"
        >
          Penetapan Karyawan
        </button>
        <button
          :class="{ active: compensationView === 'types' }"
          @click="compensationView = 'types'"
        >
          Master Komponen
        </button>
      </div>
      <div v-if="compensationView === 'assignments'" class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Karyawan</th>
              <th>Komponen</th>
              <th>Kategori</th>
              <th>Frekuensi</th>
              <th>Nominal</th>
              <th>Status Payroll</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in compensationAssignments" :key="row.id">
              <td>
                <b>{{ row.employee_name }}</b
                ><small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ row.remuneration_type_name }}</td>
              <td>{{ compensationCategoryLabel(row.workforce_category) }}</td>
              <td>{{ compensationPeriodLabel(row.period) }}</td>
              <td><b>{{ money(row.ammount) }}</b></td>
              <td>
                <span
                  :class="['status-chip', row.include_in_workforce ? 'approved' : 'pending']"
                >{{ row.include_in_workforce ? 'Aktif' : 'Legacy/Nonaktif' }}</span>
              </td>
              <td>
                <div class="row-actions">
                  <button class="icon-btn" title="Ubah" @click="openCompensationAssignment(row)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="reject-mini" title="Hapus" @click="deleteCompensationAssignment(row)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!compensationAssignments.length">
              <td colspan="7" class="wf-empty">Belum ada kompensasi yang ditetapkan.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Nama Komponen</th>
              <th>Kategori</th>
              <th>Frekuensi</th>
              <th>Integrasi Workforce</th>
              <th>Deskripsi</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in compensationTypes" :key="row.id">
              <td><b>{{ row.name }}</b></td>
              <td>{{ compensationCategoryLabel(row.workforce_category) }}</td>
              <td>{{ compensationPeriodLabel(row.period) }}</td>
              <td>
                <span :class="['status-chip', row.include_in_workforce ? 'approved' : 'pending']">
                  {{ row.include_in_workforce ? 'Aktif' : 'Perlu dipetakan' }}
                </span>
              </td>
              <td>{{ row.description || '-' }}</td>
              <td>
                <div class="row-actions">
                  <button class="icon-btn" title="Ubah" @click="openCompensationType(row)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="reject-mini" title="Hapus" @click="deleteCompensationType(row)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!compensationTypes.length">
              <td colspan="6" class="wf-empty">Belum ada master komponen kompensasi.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'loans'" class="wf-card">
      <header>
        <div>
          <small>KASBON KARYAWAN</small>
          <h3>Pinjaman & cicilan payroll</h3>
        </div>
        <button class="primary-btn" @click="openLoan">
          <i class="fas fa-plus"></i> Catat Pinjaman
        </button>
      </header>
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Nomor</th>
              <th>Karyawan</th>
              <th>Tanggal</th>
              <th>Pokok</th>
              <th>Cicilan</th>
              <th>Sisa</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in loans" :key="row.id">
              <td>
                <b>{{ row.number }}</b>
              </td>
              <td>
                {{ row.employee_name
                }}<small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ date(row.loan_date) }}</td>
              <td>{{ money(row.principal) }}</td>
              <td>{{ money(row.installment_amount) }}</td>
              <td>
                <b>{{ money(row.outstanding) }}</b>
              </td>
              <td>
                <span :class="['status-chip', row.status]">{{
                  statusLabel(row.status)
                }}</span>
              </td>
              <td>
                <button
                  v-if="row.status === 'active'"
                  class="soft-btn compact-btn"
                  @click="openLoanRepayment(row)"
                >
                  <i class="fas fa-money-bill-wave"></i> Bayar
                </button>
              </td>
            </tr>
            <tr v-if="!loans.length">
              <td colspan="8" class="wf-empty">Belum ada pinjaman karyawan.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'adjustments'" class="wf-card">
      <header>
        <div>
          <small>KOREKSI TERKONTROL</small>
          <h3>Penyesuaian payroll periode berikutnya</h3>
        </div>
        <button class="primary-btn" @click="openAdjustment">
          <i class="fas fa-plus"></i> Tambah Penyesuaian
        </button>
      </header>
      <div class="notice">
        <i class="fas fa-shield-alt"></i>
        <div>
          <b>Payroll yang sudah dibayar tidak diubah</b>
          <p>
            Selisih pendapatan atau potongan dicatat di sini dan otomatis masuk
            ke payroll berikutnya agar audit trail serta jurnal tetap utuh.
          </p>
        </div>
      </div>
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Efektif</th>
              <th>Karyawan</th>
              <th>Jenis</th>
              <th>Nominal</th>
              <th>Alasan</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in adjustments" :key="row.id">
              <td>{{ date(row.effective_date) }}</td>
              <td>
                {{ row.employee_name
                }}<small>{{ row.employee_number_id }}</small>
              </td>
              <td>{{ row.type === "earning" ? "Pendapatan" : "Potongan" }}</td>
              <td>
                <b>{{ money(row.amount) }}</b>
              </td>
              <td>{{ row.reason }}</td>
              <td>
                <span :class="['status-chip', row.status]">{{
                  statusLabel(row.status)
                }}</span>
              </td>
              <td>
                <button
                  v-if="row.status === 'pending'"
                  class="reject-mini"
                  @click="cancelAdjustment(row.id)"
                >
                  <i class="fas fa-times"></i></button
                ><span v-else>-</span>
              </td>
            </tr>
            <tr v-if="!adjustments.length">
              <td colspan="7" class="wf-empty">
                Belum ada penyesuaian payroll.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="bpjsReviewStale" class="notice warning-notice">
        <i class="fas fa-exclamation-triangle"></i>
        <div>
          <b>Parameter BPJS perlu ditinjau</b>
          <p>
            Tanggal review terakhir sudah lebih dari 12 bulan. Cocokkan batas
            upah dan tarif dengan publikasi resmi BPJS sebelum payroll
            berikutnya.
          </p>
        </div>
      </div>
    </section>

    <section v-if="activeTab === 'reports'" class="wf-card">
      <header>
        <div>
          <small>LAPORAN SDM</small>
          <h3>Ringkasan payroll & kewajiban</h3>
        </div>
        <div class="report-filter">
          <input v-model="reportFilter.from" type="date" /><input
            v-model="reportFilter.to"
            type="date"
          /><button class="primary-btn" @click="loadReport">
            <i class="fas fa-filter"></i> Terapkan</button
          ><button class="soft-btn" @click="exportPayrollReport">
            <i class="fas fa-file-csv"></i> Ekspor</button
          ><button class="soft-btn" @click="printPayrollReport">
            <i class="fas fa-print"></i> Cetak
          </button>
        </div>
      </header>
      <div class="wf-kpis compact">
        <article>
          <div>
            <small>Bruto</small><b>{{ money(report.summary.gross) }}</b>
          </div>
        </article>
        <article>
          <div>
            <small>Potongan</small><b>{{ money(report.summary.deductions) }}</b>
          </div>
        </article>
        <article>
          <div>
            <small>BPJS</small><b>{{ money(report.summary.bpjs) }}</b>
          </div>
        </article>
        <article>
          <div>
            <small>PPh 21</small><b>{{ money(report.summary.tax) }}</b>
          </div>
        </article>
        <article>
          <div>
            <small>Take-home Pay</small><b>{{ money(report.summary.net) }}</b>
          </div>
        </article>
      </div>
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>NIP & Karyawan</th>
              <th>Bruto</th>
              <th>BPJS</th>
              <th>PPh 21</th>
              <th>Take-home Pay</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.employees" :key="row.employee_id">
              <td>
                {{ row.employee_name }}<small>{{ row.employee_number }}</small>
              </td>
              <td>{{ money(row.gross_amount) }}</td>
              <td>{{ money(row.bpjs_amount) }}</td>
              <td>{{ money(row.tax_amount) }}</td>
              <td>
                <b>{{ money(row.net_amount) }}</b>
              </td>
            </tr>
            <tr v-if="!report.employees.length">
              <td colspan="5" class="wf-empty">
                Belum ada data pada periode ini.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'settings'" class="wf-card settings-card">
      <header>
        <div>
          <small>PARAMETER PAYROLL</small>
          <h3>Aturan payroll & dokumen</h3>
        </div>
        <button class="primary-btn" @click="saveSettings">
          <i class="fas fa-save"></i> Simpan Pengaturan
        </button>
      </header>
      <div class="settings-grid">
        <label v-for="item in settings" :key="item.key"
          ><span>{{ item.label }}</span
          ><small>{{ item.description }}</small>
          <div class="setting-input">
            <select v-if="item.key === 'pph21_mode'" v-model="item.value">
              <option value="ter">TER otomatis</option>
              <option value="manual">Manual</option>
            </select>
            <select
              v-else-if="item.key === 'employee_commission_slip_mode'"
              v-model="item.value"
            >
              <option value="combined">Gabung dengan slip gaji</option>
              <option value="separate">Slip komisi terpisah</option>
            </select>
            <select v-else-if="item.type === 'boolean'" v-model="item.value">
              <option value="1">Aktif</option>
              <option value="0">Nonaktif</option>
            </select>
            <input
              v-else-if="item.type === 'date'"
              v-model="item.value"
              type="date"
            />
            <input v-else v-model="item.value" type="number" step="0.1" /><em
              v-if="['percentage', 'currency'].includes(item.type)"
              >{{
                item.type === "percentage"
                  ? "%"
                  : item.type === "currency"
                  ? "Rp"
                  : ""
              }}</em
            >
          </div></label
        >
      </div>
      <div class="notice">
        <i class="fas fa-info-circle"></i>
        <div>
          <b>PPh 21 TER otomatis dan tetap dapat direview</b>
          <p>
            Tarif bulanan memakai kategori PTKP sesuai PP 58/2023. Masa pajak
            terakhir direkonsiliasi dengan tarif Pasal 17 sesuai PMK 168/2023;
            petugas payroll tetap dapat melakukan koreksi sebelum approval.
          </p>
        </div>
      </div>
    </section>

    <b-modal
      ref="periodModal"
      centered
      hide-footer
      title="Buat Periode Payroll"
    >
      <div class="form-grid">
        <label class="full"
          >Nama periode<input
            v-model="periodForm.name"
            placeholder="Contoh: Payroll Agustus 2026" /></label
        ><label
          >Mulai<input v-model="periodForm.start_date" type="date" /></label
        ><label
          >Selesai<input v-model="periodForm.end_date" type="date" /></label
        ><label class="full"
          >Rencana pembayaran<input
            v-model="periodForm.payment_date"
            type="date"
        /></label>
      </div>
      <div class="modal-actions">
        <button class="soft-btn" @click="$refs.periodModal.hide()">Batal</button
        ><button class="primary-btn" @click="createPeriod">Buat Periode</button>
      </div>
    </b-modal>

    <b-modal
      ref="detailModal"
      size="xl"
      centered
      hide-footer
      title="Rincian Periode Payroll"
    >
      <div v-if="detail.period" class="period-detail">
        <div class="detail-head">
          <div>
            <small>{{ detail.period.number }}</small>
            <h3>{{ detail.period.name }}</h3>
            <p>
              {{ date(detail.period.start_date) }} –
              {{ date(detail.period.end_date) }}
            </p>
          </div>
          <span :class="['status-chip', detail.period.status]">{{
            statusLabel(detail.period.status)
          }}</span>
        </div>
        <div class="total-grid">
          <div>
            <small>Bruto</small><b>{{ money(detail.period.gross_amount) }}</b>
          </div>
          <div>
            <small>Potongan</small
            ><b>{{ money(detail.period.deduction_amount) }}</b>
          </div>
          <div class="net">
            <small>Take-home pay</small
            ><b>{{ money(detail.period.net_amount) }}</b>
          </div>
        </div>
        <div class="table-responsive detail-table">
          <table>
            <thead>
              <tr>
                <th>Karyawan</th>
                <th>Gaji Pokok</th>
                <th>Lembur</th>
                <th>Komisi</th>
                <th>Penyesuaian</th>
                <th>BPJS</th>
                <th>PPh 21</th>
                <th>Potongan</th>
                <th>THP</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in detail.employees" :key="row.id">
                <td>
                  <b>{{ row.employee_name }}</b
                  ><small>{{ row.employee_number }}</small>
                </td>
                <td>{{ money(row.base_salary) }}</td>
                <td>{{ money(row.overtime_amount) }}</td>
                <td>{{ money(row.commission_amount) }}</td>
                <td>
                  {{
                    money(
                      Number(row.other_earning_amount || 0) -
                        Number(row.other_deduction_amount || 0)
                    )
                  }}
                </td>
                <td>{{ money(row.bpjs_employee_amount) }}</td>
                <td>
                  {{ money(row.pph21_amount)
                  }}<small v-if="row.tax_method === 'ter'"
                    >TER {{ Number(row.tax_rate || 0) }}%</small
                  >
                </td>
                <td>{{ money(totalDeduction(row)) }}</td>
                <td>
                  <b>{{ money(row.net_amount) }}</b>
                </td>
                <td>
                  <div class="row-actions">
                    <button
                      v-if="detail.period.status === 'calculated'"
                      class="icon-btn"
                      title="Review rincian"
                      @click="editPayrollLine(row)"
                    >
                      <i class="fas fa-edit"></i></button
                    ><button
                      class="icon-btn"
                      title="Buka slip"
                      @click="openPayslip(row)"
                    >
                      <i class="fas fa-file-invoice-dollar"></i>
                    </button>
                    <button
                      v-if="
                        detail.commission_slip_mode === 'separate' &&
                          Number(row.commission_amount || 0) > 0
                      "
                      class="icon-btn commission-slip-btn"
                      title="Buka slip komisi"
                      @click="openCommissionSlip(row)"
                    >
                      <i class="fas fa-receipt"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="detail.period.status === 'paid'" class="statutory-box">
          <div>
            <small>KEWAJIBAN PASCA-PAYROLL</small
            ><b
              >BPJS {{ money(statutory.bpjs_due) }} · PPh 21
              {{ money(statutory.tax_due) }}</b
            >
            <small :class="{ overdue: statutory.is_overdue }"
              >Batas setor {{ date(statutory.due_date)
              }}<template v-if="statutory.is_overdue">
                · Terlambat</template
              ></small
            >
          </div>
          <div>
            <button
              v-if="statutory.bpjs_due && !isStatutoryPaid('bpjs')"
              class="soft-btn"
              @click="payStatutory('bpjs')"
            >
              Setor BPJS</button
            ><button
              v-if="statutory.tax_due && !isStatutoryPaid('tax')"
              class="soft-btn"
              @click="payStatutory('tax')"
            >
              Setor PPh 21
            </button>
          </div>
        </div>
        <details v-if="detail.audit && detail.audit.length" class="audit-panel">
          <summary><i class="fas fa-history"></i> Jejak Audit Payroll</summary>
          <div v-for="item in detail.audit" :key="item.id" class="audit-row">
            <span class="audit-dot"></span>
            <div>
              <b>{{ auditLabel(item.action) }}</b>
              <small>
                {{ dateTime(item.created_at) }} ·
                {{ item.user_name || auditUser(item.user_id) }}
              </small>
              <p v-if="item.notes">{{ item.notes }}</p>
            </div>
          </div>
        </details>
        <div class="workflow-actions">
          <button
            v-if="['approved', 'paid'].includes(detail.period.status)"
            class="soft-btn"
            @click="exportBankTransfer"
          >
            <i class="fas fa-file-export"></i> Ekspor Transfer Bank
          </button>
          <button
            v-if="['draft', 'calculated'].includes(detail.period.status)"
            class="reject-btn"
            @click="cancelPeriod"
          >
            <i class="fas fa-ban"></i> Batalkan Periode
          </button>
          <button
            v-if="['draft', 'calculated'].includes(detail.period.status)"
            class="soft-btn"
            @click="calculate"
          >
            <i class="fas fa-calculator"></i> Hitung Ulang</button
          ><button
            v-if="detail.period.status === 'calculated'"
            class="primary-btn"
            @click="approve"
          >
            <i class="fas fa-check-double"></i> Setujui Payroll</button
          ><button
            v-if="detail.period.status === 'approved'"
            class="reject-btn"
            @click="reverseApproval"
          >
            <i class="fas fa-undo-alt"></i> Batalkan Persetujuan</button
          ><button
            v-if="detail.period.status === 'approved'"
            class="pay-btn"
            @click="pay"
          >
            <i class="fas fa-university"></i> Bayar & Kunci
          </button>
        </div>
      </div>
    </b-modal>
    <b-modal
      ref="lineModal"
      centered
      hide-footer
      title="Review Rincian Payroll"
    >
      <div v-if="lineForm" class="form-grid">
        <label class="full"
          >Karyawan<input :value="lineForm.employee_name" disabled /></label
        ><label
          >Tunjangan<input
            v-model="lineForm.allowance_amount"
            type="number" /></label
        ><label
          >Pendapatan lain<input
            v-model="lineForm.other_earning_amount"
            type="number" /></label
        ><label
          >Potongan kehadiran<input
            v-model="lineForm.attendance_deduction"
            type="number" /></label
        ><label
          >PPh 21<input v-model="lineForm.pph21_amount" type="number" /></label
        ><label
          >Potongan kasbon<input
            v-model="lineForm.loan_deduction"
            type="number" /></label
        ><label
          >Potongan lain<input
            v-model="lineForm.other_deduction_amount"
            type="number"
        /></label>
      </div>
      <div class="modal-actions">
        <button class="soft-btn" @click="$refs.lineModal.hide()">Batal</button
        ><button class="primary-btn" @click="savePayrollLine">
          <i class="fas fa-save"></i> Simpan Review
        </button>
      </div>
    </b-modal>
    <b-modal
      ref="compensationTypeModal"
      centered
      hide-footer
      title="Komponen Kompensasi"
    >
      <div v-if="compensationTypeForm" class="form-grid">
        <label class="full"
          >Nama komponen<input v-model="compensationTypeForm.name"
        /></label>
        <label
          >Kategori<select v-model="compensationTypeForm.workforce_category">
            <option value="earning">Pendapatan</option>
            <option value="deduction">Potongan</option>
          </select></label
        >
        <label
          >Frekuensi<select v-model="compensationTypeForm.period">
            <option :value="1">Bulanan</option>
            <option :value="2">3 bulanan</option>
            <option :value="3">6 bulanan</option>
            <option :value="4">Tahunan (Desember)</option>
          </select></label
        >
        <label class="full check-label"
          ><input
            v-model="compensationTypeForm.include_in_workforce"
            type="checkbox"
          />
          Aktifkan dalam kalkulasi Workforce Payroll</label
        >
        <label class="full"
          >Deskripsi<textarea
            v-model="compensationTypeForm.description"
            rows="3"
          ></textarea>
        </label>
      </div>
      <div class="modal-actions">
        <button class="soft-btn" @click="$refs.compensationTypeModal.hide()">
          Batal
        </button>
        <button class="primary-btn" @click="saveCompensationType">
          Simpan Komponen
        </button>
      </div>
    </b-modal>
    <b-modal
      ref="compensationAssignmentModal"
      centered
      hide-footer
      title="Kompensasi Karyawan"
    >
      <div v-if="compensationAssignmentForm" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="compensationAssignmentForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :key="e.id" :value="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        >
        <label class="full"
          >Komponen<select
            v-model="compensationAssignmentForm.remuneration_type_id"
          >
            <option value="">Pilih komponen</option>
            <option v-for="item in compensationTypes" :key="item.id" :value="item.id">
              {{ item.name }} · {{ compensationCategoryLabel(item.workforce_category) }}
            </option>
          </select></label
        >
        <label class="full"
          >Nominal<input
            v-model="compensationAssignmentForm.amount"
            type="number"
            min="0"
        /></label>
      </div>
      <div class="modal-actions">
        <button
          class="soft-btn"
          @click="$refs.compensationAssignmentModal.hide()"
        >
          Batal
        </button>
        <button class="primary-btn" @click="saveCompensationAssignment">
          Simpan Penetapan
        </button>
      </div>
    </b-modal>
    <b-modal ref="operationModal" centered hide-footer :title="operationTitle">
      <div v-if="operationType === 'leave'" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="operationForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :value="e.id" :key="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        ><label class="full"
          >Jenis<select v-model="operationForm.leave_type">
            <option value="annual">Cuti Tahunan</option>
            <option value="sick">Sakit</option>
            <option value="permission">Izin</option>
            <option value="maternity">Melahirkan</option>
            <option value="unpaid">Cuti di luar tanggungan</option>
          </select></label
        ><label
          >Mulai<input v-model="operationForm.start_date" type="date" /></label
        ><label
          >Selesai<input v-model="operationForm.end_date" type="date" /></label
        ><label class="full"
          >Alasan<textarea v-model="operationForm.reason" rows="3"></textarea>
        </label>
      </div>
      <div v-if="operationType === 'overtime'" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="operationForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :value="e.id" :key="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        ><label
          >Tanggal<input v-model="operationForm.work_date" type="date" /></label
        ><label
          >Mulai<input
            v-model="operationForm.start_at"
            type="datetime-local" /></label
        ><label
          >Selesai<input
            v-model="operationForm.end_at"
            type="datetime-local" /></label
        ><label class="full"
          >Alasan<textarea v-model="operationForm.reason" rows="3"></textarea>
        </label>
      </div>
      <div v-if="operationType === 'shift'" class="form-grid">
        <label>Kode<input v-model="operationForm.code" maxlength="24" /></label
        ><label>Nama Shift<input v-model="operationForm.name" /></label
        ><label
          >Mulai<input v-model="operationForm.start_time" type="time" /></label
        ><label
          >Selesai<input v-model="operationForm.end_time" type="time" /></label
        ><label
          >Istirahat (menit)<input
            v-model="operationForm.break_minutes"
            type="number" /></label
        ><label
          >Toleransi terlambat<input
            v-model="operationForm.late_tolerance_minutes"
            type="number"
        /></label>
      </div>
      <div v-if="operationType === 'loan'" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="operationForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :value="e.id" :key="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        ><label
          >Tanggal<input v-model="operationForm.loan_date" type="date" /></label
        ><label
          >Pokok Pinjaman<input
            v-model="operationForm.principal"
            type="number" /></label
        ><label
          >Cicilan per Payroll<input
            v-model="operationForm.installment_amount"
            type="number" /></label
        ><label class="full"
          >Catatan<textarea v-model="operationForm.notes" rows="3"></textarea>
        </label>
      </div>
      <div v-if="operationType === 'repayment'" class="form-grid">
        <label class="full"
          >Kasbon<input :value="operationForm.loan_label" disabled
        /></label>
        <label
          >Tanggal pembayaran<input
            v-model="operationForm.payment_date"
            type="date"
        /></label>
        <label
          >Nominal<input
            v-model="operationForm.amount"
            type="number"
            min="1"
            :max="operationForm.outstanding"
        /></label>
        <label class="full"
          >Referensi<input
            v-model="operationForm.reference_number"
            placeholder="Nomor transfer atau bukti kas"
        /></label>
        <label class="full"
          >Catatan<textarea v-model="operationForm.notes" rows="2"></textarea>
        </label>
      </div>
      <div v-if="operationType === 'assignment'" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="operationForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :value="e.id" :key="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        >
        <label class="full"
          >Shift<select v-model="operationForm.work_shift_id">
            <option value="">Pilih shift</option>
            <option v-for="s in shifts" :value="s.id" :key="s.id">
              {{ s.code }} · {{ s.name }} ({{
                String(s.start_time).slice(0, 5)
              }}–{{ String(s.end_time).slice(0, 5) }})
            </option>
          </select></label
        >
        <label
          >Mulai<input v-model="operationForm.start_date" type="date" /></label
        ><label
          >Selesai<input v-model="operationForm.end_date" type="date"
        /></label>
        <label class="full check-label"
          ><input v-model="operationForm.include_weekend" type="checkbox" />
          Sertakan Sabtu & Minggu</label
        ><label class="full"
          >Catatan<textarea v-model="operationForm.notes" rows="2"></textarea>
        </label>
      </div>
      <div v-if="operationType === 'adjustment'" class="form-grid">
        <label class="full"
          >Karyawan<select v-model="operationForm.employee_id">
            <option value="">Pilih karyawan</option>
            <option v-for="e in employeeOptions" :value="e.id" :key="e.id">
              {{ e.employee_number_id }} · {{ e.name }}
            </option>
          </select></label
        >
        <label
          >Tanggal efektif<input
            v-model="operationForm.effective_date"
            type="date" /></label
        ><label
          >Jenis<select v-model="operationForm.type">
            <option value="earning">Pendapatan tambahan</option>
            <option value="deduction">Potongan tambahan</option>
          </select></label
        >
        <label class="full"
          >Nominal<input
            v-model="operationForm.amount"
            type="number"
            min="1" /></label
        ><label class="full"
          >Alasan<textarea v-model="operationForm.reason" rows="3"></textarea>
        </label>
      </div>
      <div class="modal-actions">
        <button class="soft-btn" @click="$refs.operationModal.hide()">
          Batal</button
        ><button class="primary-btn" @click="saveOperation">Simpan</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import PageHeader from "@/component/general/PageHeader.vue";
import WorkspaceTabs from "@/component/general/WorkspaceTabs.vue";
import EmployeeTable from "@/component/human-resource/Table.vue";
import AttendanceReport from "@/view/pages/human-resource/attendance/Report.vue";
const PeriodTable = {
  props: ["items"],
  template: `<div class="table-responsive"><table><thead><tr><th>Periode</th><th>Tanggal</th><th>Bruto</th><th>Potongan</th><th>Take-home Pay</th><th>Status</th><th>Aksi</th></tr></thead><tbody><tr v-for="row in items" :key="row.id"><td><b>{{row.name}}</b><small>{{row.number}}</small></td><td>{{format(row.start_date)}} – {{format(row.end_date)}}</td><td>{{money(row.gross_amount)}}</td><td>{{money(row.deduction_amount)}}</td><td><b>{{money(row.net_amount)}}</b></td><td><span :class="['status-chip',row.status]">{{label(row.status)}}</span></td><td><button class="icon-btn" @click="$emit('open',row.id)"><i class="fas fa-arrow-right"></i></button></td></tr><tr v-if="!items.length"><td colspan="7" class="wf-empty">Belum ada periode payroll.</td></tr></tbody></table></div>`,
  methods: {
    money(v) {
      return "Rp " + Number(v || 0).toLocaleString("id-ID");
    },
    format(v) {
      return v
        ? new Date(v + "T00:00:00").toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-";
    },
    label(v) {
      return (
        {
          draft: "Draft",
          calculated: "Dihitung",
          approved: "Disetujui",
          paid: "Dibayar",
        }[v] || v
      );
    },
  },
};
export default {
  name: "WorkforceCenter",
  components: {
    PageHeader,
    WorkspaceTabs,
    PeriodTable,
    EmployeeTable,
    AttendanceReport,
  },
  data: () => ({
    activeTab: "dashboard",
    tabs: [
      { key: "dashboard", label: "Ringkasan", icon: "fas fa-th-large" },
      { key: "employees", label: "Karyawan", icon: "fas fa-user-tie" },
      { key: "time", label: "Kehadiran & Jadwal", icon: "fas fa-user-clock" },
      { key: "compensation", label: "Kompensasi", icon: "fas fa-coins" },
      { key: "payroll", label: "Payroll", icon: "fas fa-money-check-alt" },
      { key: "loans", label: "Kasbon", icon: "fas fa-hand-holding-usd" },
      {
        key: "adjustments",
        label: "Penyesuaian",
        icon: "fas fa-balance-scale",
      },
      { key: "reports", label: "Laporan", icon: "fas fa-chart-bar" },
      { key: "settings", label: "Pengaturan", icon: "fas fa-sliders-h" },
    ],
    summary: {},
    periods: [],
    credentialAlerts: [],
    employees: [],
    employeeView: "directory",
    timeView: "attendance-report",
    employeeOptions: [],
    leaves: [],
    overtimes: [],
    shifts: [],
    shiftAssignments: [],
    loans: [],
    adjustments: [],
    compensationTypes: [],
    compensationAssignments: [],
    compensationView: "assignments",
    compensationTypeForm: null,
    compensationAssignmentForm: null,
    reportFilter: {
      from: `${new Date().getFullYear()}-01-01`,
      to: `${new Date().getFullYear()}-12-31`,
    },
    report: { summary: {}, periods: [], employees: [] },
    settings: [],
    statutory: {
      bpjs_due: 0,
      tax_due: 0,
      payments: [],
      due_date: null,
      is_overdue: false,
    },
    search: "",
    searchTimer: null,
    operationType: "",
    operationTitle: "",
    operationForm: {},
    periodForm: { name: "", start_date: "", end_date: "", payment_date: "" },
    detail: {
      period: null,
      employees: [],
      audit: [],
      commission_slip_mode: "combined",
    },
    lineForm: null,
  }),
  computed: {
    bpjsReviewStale() {
      const item = this.settings.find(
        (setting) => setting.key === "bpjs_parameter_review_date"
      );
      if (!item || !item.value) return true;
      const reviewed = new Date(item.value + "T00:00:00");
      const age = Date.now() - reviewed.getTime();
      return age > 365 * 24 * 60 * 60 * 1000;
    },
  },
  watch: {
    "$route.query.tab"(value) {
      if (value && this.tabs.some((tab) => tab.key === value)) {
        this.activeTab = value;
        this.loadTab();
      }
    },
  },
  mounted() {
    const requestedTab = this.$route.query.tab;
    if (requestedTab && this.tabs.some((tab) => tab.key === requestedTab)) {
      this.activeTab = requestedTab;
    }
    this.loadDashboard();
    this.loadEmployeeOptions();
    if (this.activeTab !== "dashboard") this.loadTab();
  },
  methods: {
    selectTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.loadTab();
    },
    async loadDashboard() {
      const r = await ApiService.get("workforce/dashboard");
      if (r && r.data) {
        this.summary = r.data.summary || {};
        this.periods = r.data.periods || [];
        this.credentialAlerts = r.data.credential_alerts || [];
      }
    },
    loadTab() {
      if (this.activeTab === "dashboard") this.loadDashboard();
      if (this.activeTab === "payroll") this.loadPeriods();
      if (this.activeTab === "employees") this.loadEmployees();
      if (this.activeTab === "time") this.loadTimeView();
      if (this.activeTab === "compensation") this.loadCompensation();
      if (this.activeTab === "loans") this.loadLoans();
      if (this.activeTab === "adjustments") this.loadAdjustments();
      if (this.activeTab === "reports") this.loadReport();
      if (this.activeTab === "settings") this.loadSettings();
    },
    loadTimeView() {
      if (this.timeView === "leave") this.loadLeaves();
      if (this.timeView === "overtime") this.loadOvertimes();
      if (this.timeView === "shifts") this.loadShifts();
    },
    async loadPeriods() {
      const r = await ApiService.get("workforce/payroll-periods?page_size=50");
      this.periods = r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadEmployees() {
      const r = await ApiService.query("workforce/employees", {
        params: { search: this.search, page_size: 50 },
      });
      this.employees = r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadEmployeeOptions() {
      const r = await ApiService.get("workforce/employees?page_size=100");
      this.employeeOptions =
        r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    searchEmployees() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.loadEmployees, 350);
    },
    async loadLeaves() {
      const r = await ApiService.get("workforce/leave-requests?page_size=50");
      this.leaves = r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadOvertimes() {
      const r = await ApiService.get("workforce/overtimes?page_size=50");
      this.overtimes = r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadShifts() {
      const r = await ApiService.get("workforce/shifts");
      this.shifts = r && r.data ? r.data.data || [] : [];
      const assignments = await ApiService.get("workforce/shift-assignments");
      this.shiftAssignments =
        assignments && assignments.data ? assignments.data.data || [] : [];
    },
    async loadLoans() {
      const r = await ApiService.get("workforce/loans?page_size=50");
      this.loans = r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadSettings() {
      const r = await ApiService.get("workforce/payroll-settings");
      this.settings = r && r.data ? r.data.data || [] : [];
      if (
        !this.settings.some(
          (item) => item.key === "employee_commission_slip_mode"
        )
      ) {
        this.settings.push({
          key: "employee_commission_slip_mode",
          value: "combined",
          type: "select",
          label: "Penyajian slip komisi pegawai",
          description:
            "Pilih komisi ditampilkan di slip gaji atau disertai slip komisi tersendiri",
        });
      }
    },
    async loadAdjustments() {
      const r = await ApiService.get(
        "workforce/payroll-adjustments?page_size=100"
      );
      this.adjustments =
        r && r.data && r.data.data ? r.data.data.data || [] : [];
    },
    async loadCompensation() {
      const r = await ApiService.get("workforce/compensation");
      this.compensationTypes = r && r.data ? r.data.types || [] : [];
      this.compensationAssignments =
        r && r.data ? r.data.assignments || [] : [];
    },
    openCompensationType(row) {
      this.compensationTypeForm = row
        ? {
            id: row.id,
            name: row.name,
            description: row.description || "",
            period: Number(row.period || 1),
            workforce_category: row.workforce_category || "earning",
            include_in_workforce: Boolean(row.include_in_workforce),
          }
        : {
            id: null,
            name: "",
            description: "",
            period: 1,
            workforce_category: "earning",
            include_in_workforce: true,
          };
      this.$refs.compensationTypeModal.show();
    },
    async saveCompensationType() {
      if (!this.compensationTypeForm || !this.compensationTypeForm.name) {
        return this.notify("Nama komponen wajib diisi", "warning");
      }
      const form = {
        ...this.compensationTypeForm,
        include_in_workforce: Boolean(
          this.compensationTypeForm.include_in_workforce
        ),
      };
      const endpoint = form.id
        ? `workforce/compensation/types/${form.id}`
        : "workforce/compensation/types";
      const r = form.id
        ? await ApiService.put(endpoint, form)
        : await ApiService.post(endpoint, form);
      if (!r || r.status >= 400)
        return this.notify(
          (r && r.data && r.data.message) || "Komponen gagal disimpan",
          "error"
        );
      this.$refs.compensationTypeModal.hide();
      await this.loadCompensation();
      this.notify(r.data.message, "success");
    },
    async deleteCompensationType(row) {
      const result = await this.$swal({
        title: "Hapus komponen kompensasi?",
        text: row.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Hapus",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;
      const r = await ApiService.delete(
        `workforce/compensation/types/${row.id}`
      );
      if (!r || r.status >= 400)
        return this.notify(
          (r && r.data && r.data.message) || "Komponen tidak dapat dihapus",
          "error"
        );
      await this.loadCompensation();
      this.notify(r.data.message, "success");
    },
    openCompensationAssignment(row) {
      this.compensationAssignmentForm = row
        ? {
            id: row.id,
            employee_id: row.employee_id,
            remuneration_type_id: row.remuneration_type_id,
            amount: Number(row.ammount || 0),
          }
        : {
            id: null,
            employee_id: "",
            remuneration_type_id: "",
            amount: 0,
          };
      this.$refs.compensationAssignmentModal.show();
    },
    async saveCompensationAssignment() {
      const form = this.compensationAssignmentForm;
      if (!form || !form.employee_id || !form.remuneration_type_id) {
        return this.notify("Karyawan dan komponen wajib dipilih", "warning");
      }
      const endpoint = form.id
        ? `workforce/compensation/assignments/${form.id}`
        : "workforce/compensation/assignments";
      const r = form.id
        ? await ApiService.put(endpoint, form)
        : await ApiService.post(endpoint, form);
      if (!r || r.status >= 400)
        return this.notify(
          (r && r.data && r.data.message) || "Kompensasi gagal disimpan",
          "error"
        );
      this.$refs.compensationAssignmentModal.hide();
      await this.loadCompensation();
      this.notify(r.data.message, "success");
    },
    async deleteCompensationAssignment(row) {
      const result = await this.$swal({
        title: "Hapus penetapan kompensasi?",
        text: `${row.employee_name} · ${row.remuneration_type_name}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Hapus",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;
      const r = await ApiService.delete(
        `workforce/compensation/assignments/${row.id}`
      );
      if (!r || r.status >= 400)
        return this.notify(
          (r && r.data && r.data.message) || "Penetapan tidak dapat dihapus",
          "error"
        );
      await this.loadCompensation();
      this.notify(r.data.message, "success");
    },
    async loadReport() {
      const r = await ApiService.query("workforce/reports/payroll", {
        params: this.reportFilter,
      });
      this.report =
        r && r.data ? r.data : { summary: {}, periods: [], employees: [] };
    },
    exportPayrollReport() {
      const rows = [
        ["NIP", "Karyawan", "Bruto", "BPJS", "PPh 21", "Take-home Pay"],
        ...(this.report.employees || []).map((row) => [
          row.employee_number || "",
          row.employee_name || "",
          Number(row.gross_amount || 0),
          Number(row.bpjs_amount || 0),
          Number(row.tax_amount || 0),
          Number(row.net_amount || 0),
        ]),
      ];
      const csv = rows
        .map((row) =>
          row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" })
      );
      link.download = `laporan-payroll-${this.reportFilter.from}-${this.reportFilter.to}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    printPayrollReport() {
      window.print();
    },
    openPeriod() {
      const now = new Date(),
        y = now.getFullYear(),
        m = String(now.getMonth() + 1).padStart(2, "0");
      this.periodForm = {
        name: `Payroll ${now.toLocaleDateString("id-ID", {
          month: "long",
          year: "numeric",
        })}`,
        start_date: `${y}-${m}-01`,
        end_date: new Date(y, now.getMonth() + 1, 0).toISOString().slice(0, 10),
        payment_date: "",
      };
      this.$refs.periodModal.show();
    },
    async createPeriod() {
      try {
        const r = await ApiService.post(
          "workforce/payroll-periods",
          this.periodForm
        );
        this.$refs.periodModal.hide();
        await this.loadPeriods();
        this.openDetail(r.data.id);
        this.notify(r.data.message, "success");
      } catch (e) {
        this.notify(
          (e.response && e.response.data.message) || "Periode gagal dibuat",
          "error"
        );
      }
    },
    async openDetail(id) {
      const r = await ApiService.get("workforce/payroll-periods/" + id);
      if (r && r.data) {
        this.detail = r.data;
        this.statutory = {
          bpjs_due: 0,
          tax_due: 0,
          payments: [],
          due_date: null,
          is_overdue: false,
        };
        if (r.data.period && r.data.period.status === "paid") {
          const statutory = await ApiService.get(
            `workforce/payroll-periods/${id}/statutory-payments`
          );
          if (statutory && statutory.data) this.statutory = statutory.data;
        }
        this.$refs.detailModal.show();
      }
    },
    openPayslip(row) {
      this.$refs.detailModal.hide();
      this.$router.push(
        `/human-resource/payroll/payslip/${this.detail.period.id}/${row.id}`
      );
    },
    openCommissionSlip(row) {
      this.$refs.detailModal.hide();
      this.$router.push(
        `/human-resource/payroll/payslip/${this.detail.period.id}/${row.id}?document=commission`
      );
    },
    exportBankTransfer() {
      const incomplete = (this.detail.employees || []).filter(
        (row) => !row.bank_name || !row.bank_account_number
      );
      if (incomplete.length) {
        this.notify(
          `${incomplete.length} karyawan belum memiliki data bank lengkap. Lengkapi master karyawan sebelum mengekspor instruksi transfer.`,
          "warning"
        );
        return;
      }
      const rows = [
        [
          "NIP",
          "Nama Karyawan",
          "Bank",
          "Nomor Rekening",
          "Nama Rekening",
          "Nominal",
          "Referensi",
        ],
        ...(this.detail.employees || []).map((row) => [
          row.employee_number || "",
          row.employee_name || "",
          row.bank_name || "",
          row.bank_account_number || "",
          row.bank_account_name || row.employee_name || "",
          Number(row.net_amount || 0),
          this.detail.period.number,
        ]),
      ];
      const csv = rows
        .map((row) =>
          row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" })
      );
      link.download = `transfer-${this.detail.period.number}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    editPayrollLine(row) {
      this.lineForm = {
        ...row,
        allowance_amount: Number(row.allowance_amount || 0),
        other_earning_amount: Number(row.other_earning_amount || 0),
        attendance_deduction: Number(row.attendance_deduction || 0),
        pph21_amount: Number(row.pph21_amount || 0),
        loan_deduction: Number(row.loan_deduction || 0),
        other_deduction_amount: Number(row.other_deduction_amount || 0),
      };
      this.$refs.lineModal.show();
    },
    async savePayrollLine() {
      if (!this.lineForm || !this.detail.period) return;
      try {
        await ApiService.put(
          `workforce/payroll-periods/${this.detail.period.id}/employees/${this.lineForm.id}`,
          {
            allowance_amount: Number(this.lineForm.allowance_amount || 0),
            other_earning_amount: Number(
              this.lineForm.other_earning_amount || 0
            ),
            attendance_deduction: Number(
              this.lineForm.attendance_deduction || 0
            ),
            pph21_amount: Number(this.lineForm.pph21_amount || 0),
            loan_deduction: Number(this.lineForm.loan_deduction || 0),
            other_deduction_amount: Number(
              this.lineForm.other_deduction_amount || 0
            ),
            tax_method: this.lineForm.tax_method,
            tax_rate: Number(this.lineForm.tax_rate || 0),
          }
        );
        this.$refs.lineModal.hide();
        await this.openDetail(this.detail.period.id);
        await this.loadPeriods();
        this.notify("Rincian payroll berhasil diperbarui", "success");
      } catch (e) {
        this.notify(
          (e.response && e.response.data.message) ||
            "Rincian payroll gagal diperbarui",
          "error"
        );
      }
    },
    isStatutoryPaid(type) {
      return (this.statutory.payments || []).some((item) => item.type === type);
    },
    async payStatutory(type) {
      const label = type === "bpjs" ? "BPJS" : "PPh 21";
      const result = await this.$swal({
        title: `Setor ${label}`,
        text: "Masukkan nomor referensi pembayaran (opsional).",
        input: "text",
        inputPlaceholder: "Contoh: NTPN / nomor bukti bayar",
        showCancelButton: true,
        confirmButtonText: "Catat Pembayaran",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/statutory-payments`,
        {
          type,
          payment_date: window.medhopToday(),
          reference_number: result.value || "",
        }
      );
      await this.openDetail(this.detail.period.id);
      this.notify(`Pembayaran ${label} berhasil dicatat`, "success");
    },
    async calculate() {
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/calculate`,
        {}
      );
      await this.openDetail(this.detail.period.id);
      this.loadPeriods();
      this.notify("Payroll berhasil dihitung", "success");
    },
    async cancelPeriod() {
      const result = await this.$swal({
        title: "Batalkan periode payroll?",
        text: "Periode tidak akan diproses dan penyesuaian terkait dikembalikan ke antrean.",
        input: "textarea",
        inputLabel: "Alasan pembatalan",
        inputValidator: (value) =>
          !value || value.trim().length < 5
            ? "Alasan minimal 5 karakter"
            : undefined,
        showCancelButton: true,
        confirmButtonText: "Batalkan Periode",
        cancelButtonText: "Kembali",
      });
      if (!result.isConfirmed) return;
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/cancel`,
        { reason: result.value }
      );
      this.$refs.detailModal.hide();
      await this.loadPeriods();
      await this.loadDashboard();
      this.notify("Periode payroll berhasil dibatalkan", "success");
    },
    async approve() {
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/approve`,
        {}
      );
      await this.openDetail(this.detail.period.id);
      this.loadPeriods();
      this.notify("Payroll disetujui dan dijurnal", "success");
    },
    async pay() {
      const result = await this.$swal({
        title: "Bayar dan kunci payroll",
        text: "Pastikan file transfer dan rekening karyawan telah diverifikasi.",
        input: "date",
        inputLabel: "Tanggal pembayaran",
        inputValue:
          this.detail.period.payment_date ||
          window.medhopToday(),
        showCancelButton: true,
        confirmButtonText: "Bayar & Kunci",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/pay`,
        { payment_date: result.value }
      );
      await this.openDetail(this.detail.period.id);
      this.loadPeriods();
      this.notify("Payroll dibayar dan dikunci", "success");
    },
    async reverseApproval() {
      const result = await this.$swal({
        title: "Batalkan persetujuan payroll?",
        text: "Jurnal akrual akan direversal dan payroll kembali ke status Dihitung.",
        input: "textarea",
        inputLabel: "Alasan pembatalan",
        inputValidator: (value) =>
          !value || value.trim().length < 5
            ? "Alasan minimal 5 karakter"
            : undefined,
        showCancelButton: true,
        confirmButtonText: "Batalkan Persetujuan",
        cancelButtonText: "Kembali",
      });
      if (!result.isConfirmed) return;
      await ApiService.post(
        `workforce/payroll-periods/${this.detail.period.id}/reverse-approval`,
        { reason: result.value }
      );
      await this.openDetail(this.detail.period.id);
      await this.loadPeriods();
      this.notify("Persetujuan dibatalkan dan jurnal direversal", "success");
    },
    async saveSettings() {
      const values = {};
      this.settings.forEach((x) => (values[x.key] = x.value));
      await ApiService.put("workforce/payroll-settings", { settings: values });
      this.notify("Pengaturan payroll berhasil disimpan", "success");
    },
    openOperation(type, title, form) {
      this.operationType = type;
      this.operationTitle = title;
      this.operationForm = form;
      this.$refs.operationModal.show();
    },
    openLeave() {
      this.openOperation("leave", "Ajukan Cuti / Izin", {
        employee_id: "",
        leave_type: "annual",
        start_date: "",
        end_date: "",
        reason: "",
      });
    },
    openOvertime() {
      this.openOperation("overtime", "Ajukan Lembur", {
        employee_id: "",
        work_date: "",
        start_at: "",
        end_at: "",
        reason: "",
      });
    },
    openShift() {
      this.openOperation("shift", "Tambah Shift Kerja", {
        code: "",
        name: "",
        start_time: "09:00",
        end_time: "17:00",
        break_minutes: 60,
        late_tolerance_minutes: 5,
      });
    },
    openLoan() {
      this.openOperation("loan", "Catat Pinjaman Karyawan", {
        employee_id: "",
        loan_date: window.medhopToday(),
        principal: 0,
        installment_amount: 0,
        notes: "",
      });
    },
    openLoanRepayment(row) {
      this.openOperation("repayment", "Pembayaran Kasbon", {
        loan_id: row.id,
        loan_label: `${row.number} · ${row.employee_name} · Sisa ${this.money(
          row.outstanding
        )}`,
        payment_date: window.medhopToday(),
        amount: Math.min(
          Number(row.installment_amount || 0),
          Number(row.outstanding || 0)
        ),
        outstanding: Number(row.outstanding || 0),
        reference_number: "",
        notes: "",
      });
    },
    openShiftAssignment() {
      const today = window.medhopToday();
      this.openOperation("assignment", "Atur Jadwal Shift", {
        employee_id: "",
        work_shift_id: "",
        start_date: today,
        end_date: today,
        include_weekend: false,
        notes: "",
      });
    },
    openAdjustment() {
      this.openOperation("adjustment", "Tambah Penyesuaian Payroll", {
        employee_id: "",
        effective_date: window.medhopToday(),
        type: "earning",
        amount: 0,
        reason: "",
      });
    },
    async cancelAdjustment(id) {
      const result = await this.$swal({
        title: "Batalkan penyesuaian?",
        input: "text",
        inputLabel: "Alasan pembatalan",
        showCancelButton: true,
        confirmButtonText: "Batalkan Penyesuaian",
        cancelButtonText: "Kembali",
      });
      if (!result.isConfirmed) return;
      await ApiService.post(`workforce/payroll-adjustments/${id}/cancel`, {
        reason: result.value || "Dibatalkan oleh petugas",
      });
      this.loadAdjustments();
    },
    async saveOperation() {
      const endpoints = {
        leave: "workforce/leave-requests",
        overtime: "workforce/overtimes",
        shift: "workforce/shifts",
        loan: "workforce/loans",
        assignment: "workforce/shift-assignments",
        adjustment: "workforce/payroll-adjustments",
        repayment: `workforce/loans/${this.operationForm.loan_id}/repayments`,
      };
      try {
        const r = await ApiService.post(
          endpoints[this.operationType],
          this.operationForm
        );
        this.$refs.operationModal.hide();
        this.loadTab();
        this.notify(r.data.message, "success");
      } catch (e) {
        this.notify(
          (e.response && e.response.data.message) || "Data gagal disimpan",
          "error"
        );
      }
    },
    async decideLeave(id, status) {
      await ApiService.post(`workforce/leave-requests/${id}/decision`, {
        status,
      });
      this.loadLeaves();
    },
    async decideOvertime(row) {
      const result = await this.$swal({
        title: "Setujui lembur",
        input: "number",
        inputLabel: "Nominal lembur yang disetujui",
        inputValue: row.approved_amount || row.suggested_amount || 0,
        footer: `Rekomendasi berdasarkan upah sejam 1/173: ${this.money(
          row.suggested_amount || 0
        )}`,
        showCancelButton: true,
      });
      if (result.isConfirmed) {
        await ApiService.post(`workforce/overtimes/${row.id}/decision`, {
          status: "approved",
          approved_amount: Number(result.value) || 0,
        });
        this.loadOvertimes();
      }
    },
    async rejectOvertime(id) {
      await ApiService.post(`workforce/overtimes/${id}/decision`, {
        status: "rejected",
        approved_amount: 0,
      });
      this.loadOvertimes();
    },
    editEmployee(row) {
      this.$router.push("/human-resource/employee/edit/" + row.id);
    },
    money(v) {
      return "Rp " + Number(v || 0).toLocaleString("id-ID");
    },
    date(v) {
      return v
        ? new Date(v + "T00:00:00").toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-";
    },
    time(v) {
      return v ? String(v).slice(11, 16) : "--:--";
    },
    statusLabel(v) {
      return (
        {
          draft: "Draft",
          submitted: "Diajukan",
          calculated: "Dihitung",
          approved: "Disetujui",
          rejected: "Ditolak",
          paid: "Dibayar",
          active: "Aktif",
          closed: "Lunas",
          pending: "Menunggu",
          applied: "Diterapkan",
          cancelled: "Dibatalkan",
        }[v] || v
      );
    },
    employmentLabel(v) {
      return (
        {
          permanent: "Tetap",
          contract: "Kontrak",
          probation: "Probation",
          freelance: "Mitra",
        }[v] || v
      );
    },
    compensationCategoryLabel(v) {
      return (
        {
          earning: "Pendapatan",
          deduction: "Potongan",
        }[v] || "Belum dipetakan"
      );
    },
    compensationPeriodLabel(v) {
      return (
        {
          1: "Bulanan",
          2: "3 bulanan",
          3: "6 bulanan",
          4: "Tahunan",
        }[Number(v)] || "Bulanan"
      );
    },
    leaveLabel(v) {
      return (
        {
          annual: "Cuti Tahunan",
          sick: "Sakit",
          permission: "Izin",
          maternity: "Melahirkan",
          unpaid: "Di luar tanggungan",
        }[v] || v
      );
    },
    auditLabel(v) {
      return (
        {
          created: "Periode dibuat",
          calculated: "Payroll dihitung",
          line_updated: "Rincian direview",
          approved: "Payroll disetujui",
          paid: "Payroll dibayar dan dikunci",
          statutory_paid: "Kewajiban disetor",
          approval_reversed: "Persetujuan dibatalkan",
          cancelled: "Periode dibatalkan",
        }[v] || v
      );
    },
    auditUser(id) {
      return id ? "Pengguna #" + id : "Sistem";
    },
    dateTime(v) {
      return this.$clinicDateTime(v, { dateStyle: "medium", timeStyle: "short" });
    },
    credentialDate(row) {
      const dates = [row.str_expired_at, row.sip_expired_at]
        .filter(Boolean)
        .sort();
      return dates.length
        ? "Berlaku s/d " + this.date(dates[0])
        : "Belum dilengkapi";
    },
    totalDeduction(r) {
      return (
        Number(r.attendance_deduction || 0) +
        Number(r.bpjs_employee_amount || 0) +
        Number(r.pph21_amount || 0) +
        Number(r.loan_deduction || 0) +
        Number(r.other_deduction_amount || 0)
      );
    },
    notify(text, icon) {
      if (this.$swal) this.$swal({ text, icon, confirmButtonText: "OK" });
      else window.alert(text);
    },
  },
};
</script>

<style lang="scss">
.workforce-center {
  --navy: var(--skin-deep);
  --teal: var(--skin-primary);
  --ink: #1f2a3d;
  --line: #dce5ef;
  color: var(--ink);
}
.statutory-box {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dce5ef;
  border-radius: 12px;
  background: #f7fafc;
}
.statutory-box small {
  display: block;
  margin-top: 3px;
  color: #718096;
}
.statutory-box small.overdue {
  color: #c53030;
  font-weight: 700;
}
.warning-notice {
  border-color: #f6c453 !important;
  background: #fffaf0 !important;
  color: #8a5a00 !important;
}
.balance-chip {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 999px;
  background: #e9f7f4;
  color: var(--skin-primary);
  font-weight: 700;
  white-space: nowrap;
}
.audit-panel {
  margin-top: 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.audit-panel summary {
  padding: 12px 14px;
  color: var(--navy);
  font-weight: 700;
  cursor: pointer;
}
.audit-panel summary i {
  margin-right: 8px;
}
.audit-row {
  display: flex;
  gap: 10px;
  margin: 0 14px;
  padding: 10px 0;
  border-top: 1px solid #edf2f7;
}
.audit-row small,
.audit-row p {
  display: block;
  margin: 2px 0 0;
  color: #718096;
}
.audit-dot {
  flex: 0 0 9px;
  width: 9px;
  height: 9px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--teal);
}
.compact-btn {
  min-height: 32px;
  padding: 6px 10px;
  white-space: nowrap;
}
.reject-btn {
  min-height: 40px;
  padding: 9px 14px;
  border: 0;
  border-radius: 9px;
  background: #fff1f1;
  color: #c53030;
  font-weight: 700;
}
@media print {
  .workforce-center > :not(.wf-card),
  .workforce-center .wf-card:not([style]) .report-filter,
  .workforce-center .assistant-widget {
    display: none !important;
  }
  .workforce-center .wf-card {
    border: 0;
    box-shadow: none;
  }
}
.wf-subtabs {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
}
.wf-subtabs button {
  flex: 0 0 auto;
  padding: 9px 13px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #65748a;
  font-weight: 750;
}
.wf-subtabs button.active {
  border-color: var(--teal);
  color: #087c76;
}
.wf-inline-filter {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.workforce-time-card .attendance-card,
.workforce-time-card .report-card {
  margin-top: 0;
  border: 0;
  box-shadow: none;
}
.compensation-notice {
  margin: 0 0 16px;
  background: #eef7fb;
  color: #235b78;
}
.wf-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.wf-kpis article,
.wf-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(30, 55, 85, 0.05);
}
.wf-kpis article {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
}
.wf-kpis i {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: var(--teal);
  background: var(--skin-soft);
  font-size: 17px;
}
.wf-kpis small,
.wf-card td small {
  display: block;
  color: #7a899e;
}
.wf-kpis b {
  font-size: 24px;
}
.wf-kpis .warning i {
  background: #fff3df;
  color: #df8a00;
}
.wf-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}
.wf-card {
  padding: 18px;
  margin-bottom: 16px;
}
.wf-card > header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 15px;
}
.wf-card header small {
  color: var(--teal);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
}
.wf-card h3,
.period-detail h3 {
  font-size: 17px;
  margin: 3px 0 0;
  font-weight: 800;
}
.wf-card table {
  width: 100%;
  border-collapse: collapse;
}
.wf-card th {
  padding: 11px 13px;
  text-align: left;
  background: #f4f7fb;
  color: #5b6d85;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
}
.wf-card td {
  padding: 12px 13px;
  border-bottom: 1px solid #e8eef5;
  font-size: 13px;
}
.wf-empty {
  text-align: center !important;
  padding: 35px !important;
  color: #8492a6;
}
.credential-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #edf1f6;
}
.credential-row small {
  display: block;
  color: #8592a4;
}
.credential-row span {
  font-size: 11px;
  color: #d77b00;
}
.status-chip {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 20px;
  background: #edf3fa;
  color: #52667e;
  font-size: 11px;
  font-weight: 800;
}
.status-chip.calculated,
.status-chip.submitted {
  background: #e8f2ff;
  color: #1966aa;
}
.status-chip.approved {
  background: #e7f8f2;
  color: #11805d;
}
.status-chip.paid {
  background: #e8f8ed;
  color: #13854a;
}
.wf-search,
input {
  border: 1px solid #d7e1ec;
  border-radius: 9px;
  padding: 10px 12px;
  color: #33435a;
  outline: none;
}
.wf-search {
  width: 250px;
}
.soft-btn,
.primary-btn,
.pay-btn,
.icon-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 14px;
  font-weight: 750;
}
.soft-btn {
  background: #edf3f8;
  color: #205c87;
}
.primary-btn {
  background: linear-gradient(115deg, var(--navy), var(--teal));
  color: #fff;
}
.pay-btn {
  background: #15975e;
  color: #fff;
}
.icon-btn {
  padding: 8px 11px;
  background: #eaf3fa;
  color: #126399;
}
.settings-grid,
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.settings-grid label {
  padding: 14px;
  border: 1px solid #e1e8f0;
  border-radius: 12px;
}
.settings-grid label span,
.form-grid label {
  display: block;
  font-weight: 750;
}
.settings-grid label small {
  display: block;
  min-height: 34px;
  color: #8492a6;
}
.setting-input {
  display: flex;
  align-items: center;
  margin-top: 7px;
}
.setting-input input,
.setting-input select {
  width: 100%;
}
.setting-input select {
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid #d7e1ec;
  border-radius: 9px;
  background: #fff;
  color: #33435a;
}
.setting-input em {
  margin-left: -35px;
  font-style: normal;
  color: #73849a;
}
.notice {
  display: flex;
  gap: 12px;
  margin-top: 18px;
  padding: 14px;
  border-radius: 12px;
  background: #fff8e9;
  color: #73510d;
}
.notice p {
  margin: 3px 0 0;
}
.form-grid label input {
  width: 100%;
  margin-top: 7px;
}
.form-grid .full {
  grid-column: 1/-1;
}
.modal-actions,
.workflow-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
.detail-head,
.total-grid {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.total-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 15px 0;
}
.total-grid > div {
  padding: 12px;
  background: #f5f8fb;
  border-radius: 10px;
}
.total-grid small {
  display: block;
  color: #748398;
}
.total-grid b {
  font-size: 17px;
}
.total-grid .net {
  background: #e9f8f3;
  color: #08775b;
}
.detail-table {
  max-height: 420px;
  overflow: auto;
  border: 1px solid #e3eaf2;
  border-radius: 12px;
  background: #fff;
  scrollbar-gutter: stable;
}
.detail-table table {
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}
.detail-table th,
.detail-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #e8eef5;
  vertical-align: middle;
}
.detail-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f4f7fb;
  color: #5b6d85;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.45px;
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
}
.detail-table td {
  color: #26364a;
  font-size: 12px;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.detail-table th:first-child,
.detail-table td:first-child {
  width: 190px;
  text-align: left;
}
.detail-table th:last-child,
.detail-table td:last-child {
  width: 126px;
  text-align: center;
}
.detail-table td:first-child b,
.detail-table td:first-child small,
.detail-table td:nth-child(7) small {
  display: block;
}
.detail-table td:first-child b {
  overflow: hidden;
  text-overflow: ellipsis;
}
.detail-table td:first-child small,
.detail-table td:nth-child(7) small {
  margin-top: 3px;
  color: #7a899e;
  font-size: 10px;
}
.detail-table tbody tr:last-child td {
  border-bottom: 0;
}
.detail-table tbody tr:hover td {
  background: #f8fbfd;
}
.detail-table .row-actions {
  justify-content: center;
}
.detail-table .icon-btn {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  padding: 0;
}
.detail-table .commission-slip-btn {
  background: #e9f8f3;
  color: #08775b;
}
.period-detail p {
  margin: 4px 0;
  color: #718198;
}
@media (max-width: 900px) {
  .wf-kpis {
    grid-template-columns: 1fr 1fr;
  }
  .wf-grid {
    grid-template-columns: 1fr;
  }
  .settings-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 576px) {
  .wf-kpis {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .wf-kpis article {
    padding: 12px;
  }
  .wf-kpis b {
    font-size: 19px;
  }
  .wf-card {
    padding: 12px;
  }
  .wf-card > header {
    align-items: flex-start;
    flex-direction: column;
  }
  .wf-search {
    width: 100%;
  }
  .settings-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
  .total-grid {
    grid-template-columns: 1fr;
  }
  .workforce-center .table-responsive {
    margin: 0 -12px;
  }
}
.form-grid select,
.form-grid textarea {
  width: 100%;
  margin-top: 7px;
  border: 1px solid #d7e1ec;
  border-radius: 9px;
  padding: 10px 12px;
  background: #fff;
  color: #33435a;
}
.row-actions {
  display: flex;
  gap: 6px;
}
.approve-mini,
.reject-mini {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
}
.approve-mini {
  background: #e4f7ed;
  color: #118153;
}
.reject-mini {
  background: #feecec;
  color: #cf3f48;
}
.shift-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.shift-grid article {
  display: flex;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e0e8f0;
  border-radius: 12px;
}
.shift-grid article > i {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #e8f7f5;
  color: #108b83;
}
.shift-grid small,
.shift-grid span {
  display: block;
  color: #7b899c;
}
.shift-grid span {
  font-size: 11px;
  margin-top: 5px;
}
@media (max-width: 900px) {
  .shift-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 576px) {
  .shift-grid {
    grid-template-columns: 1fr;
  }
}
</style>
